import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type TextArea from "sap/m/TextArea";
import type Text from "sap/m/Text";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

interface ViewState {
	orderId: string;
	orderNumber: string;
	orderType: string;
	beneficiaryName: string;
	relatedOrderNumber: string;
	hasFollowUps: boolean;
	followUpsSummary: string;
}

interface OrderHeader {
	OrderNumber: string;
	OrderType: string;
	BeneficiaryOrgId: string | null;
	RelatedOrderId: string | null;
}

interface FollowUpRow {
	OrderNumber: string;
	OrderType: string;
}

/**
 * Strona szczegółów pojedynczego zamówienia (route: <c>order/{id}</c>).
 *
 * Bindowanie:
 * - Cała view zbindowana do <c>odata>/Orders(id)</c> przez bindElement w
 *   <see cref="onPatternMatched"/>; pola Header/Lines pochodzą z tej entity (Lines = nawigacja
 *   bindowana relative w XML).
 * - Pomocnicze nazwy (Beneficiary.Name, RelatedOrder.OrderNumber, lista follow-upów) są
 *   ładowane osobnymi GET'ami do view modelu — analogicznie do parent w OrganizationDetail —
 *   bo OrderODataDto.RelatedOrder nie jest projektowane przez OrdersController, a Beneficiary
 *   to inny entity set.
 *
 * Akcje (dialog flows reuse'ują podejście z Orders.controller — Fragment.load + addDependent):
 * - <c>Pay</c> — reuse PayOrderDialog (ten sam fragment co skrót na liście).
 * - <c>MarkOverdue</c> — własny fragment z polem Reason (TextArea, max 500 znaków).
 *
 * @namespace computec.b2b.portal.controller
 */
export default class OrderDetail extends BaseController {
	private payDialog?: Dialog;
	private markOverdueDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			orderId: "",
			orderNumber: "",
			orderType: "",
			beneficiaryName: "",
			relatedOrderNumber: "",
			hasFollowUps: false,
			followUpsSummary: ""
		};
		this.getView().setModel(new JSONModel(initial), "view");
		this.getRouter().getRoute("orderDetail")?.attachPatternMatched((e) => {
			this.onPatternMatched(e);
		});
	}

	private onPatternMatched(event: Route$PatternMatchedEvent): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";

		const view = this.getView().getModel("view") as JSONModel;
		const fresh: ViewState = {
			orderId: id,
			orderNumber: "",
			orderType: "",
			beneficiaryName: "",
			relatedOrderNumber: "",
			hasFollowUps: false,
			followUpsSummary: ""
		};
		view.setData(fresh);

		// bindElement: pojedyncza encja po Id; OData v4 akceptuje Guid jako literał w nawiasach
		// bez cudzysłowów dla typu Edm.Guid.
		this.getView().bindElement({
			path: `/Orders(${id})`,
			model: "odata"
		});

		// Tabela linii: read-model `OrderLineReports` (ADR-011) zamiast denormalized $expand=Lines.
		// $filter=OrderId aplikujemy dynamicznie tutaj, bo wartość pochodzi z route param.
		this.applyOrderLinesFilter(id);

		void this.loadHeaderSideData(id);
	}

	/**
	 * Aplikuje <c>$filter=OrderId eq &lt;id&gt;</c> na bindingu tabeli linii. Wywoływane
	 * w <see cref="onPatternMatched"/> po nawigacji — wartość id pochodzi z route param.
	 */
	private applyOrderLinesFilter(orderId: string): void {
		const table = this.byId("orderLinesTable") as { getBinding(name: string): { changeParameters?(p: { $filter?: string }): void } | undefined } | undefined;
		const binding = table?.getBinding("items");
		if (!binding?.changeParameters) {
			// Binding może być jeszcze nie utworzony — odłóż na następną pętlę event loopa.
			setTimeout(() => this.applyOrderLinesFilter(orderId), 0);
			return;
		}
		binding.changeParameters({ $filter: `OrderId eq ${orderId}` });
	}

	private async loadHeaderSideData(orderId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const fetcher = this.getOwnerComponent().getAuthFetch();

		try {
			const order = await fetcher.json<OrderHeader>(
				`/odata/v4/Orders(${orderId})?$select=OrderNumber,OrderType,BeneficiaryOrgId,RelatedOrderId`
			);
			view.setProperty("/orderNumber", order.OrderNumber);
			view.setProperty("/orderType", order.OrderType);

			if (order.BeneficiaryOrgId) {
				try {
					const beneficiary = await fetcher.json<{ Name: string }>(
						`/odata/v4/Organizations(${order.BeneficiaryOrgId})?$select=Name`
					);
					view.setProperty("/beneficiaryName", beneficiary.Name);
				} catch {
					// Beneficiary poza scope (np. EC widziany przez innego partnera) → 404, pokażemy GUID.
				}
			}

			if (order.RelatedOrderId) {
				try {
					const related = await fetcher.json<{ OrderNumber: string }>(
						`/odata/v4/Orders(${order.RelatedOrderId})?$select=OrderNumber`
					);
					view.setProperty("/relatedOrderNumber", related.OrderNumber);
				} catch {
					// Related poza scope — pokażemy GUID.
				}
			}
		} catch {
			// Sam Order poza scope → bindElement zwróci 404 i UI pokaże pusto; nic więcej tu nie robimy.
			return;
		}

		// Follow-upy = inne zamówienia, które wskazują na ten Id przez RelatedOrderId.
		// Lekki summary "{count} (Renewal, Cancellation, ...)" — pełna lista zostanie sekcją w przyszłej iteracji.
		try {
			const followUps = await fetcher.json<{ value: FollowUpRow[] }>(
				`/odata/v4/Orders?$filter=RelatedOrderId eq ${orderId}&$select=OrderNumber,OrderType&$orderby=PlacedAt`
			);
			if (followUps.value.length > 0) {
				const types = Array.from(new Set(followUps.value.map(o => o.OrderType))).join(", ");
				view.setProperty("/hasFollowUps", true);
				view.setProperty("/followUpsSummary", `${followUps.value.length} (${types})`);
			}
		} catch {
			// Brak danych = brak sekcji — nic nie pokazujemy.
		}
	}

	public onOpenBeneficiary(): void {
		const ctx = this.getView().getBindingContext("odata");
		const beneficiaryId = ctx?.getProperty("BeneficiaryOrgId") as string | undefined;
		if (!beneficiaryId) {
			return;
		}
		this.navToOrganization(beneficiaryId);
	}

	public onOpenRelatedOrder(): void {
		const ctx = this.getView().getBindingContext("odata");
		const relatedId = ctx?.getProperty("RelatedOrderId") as string | undefined;
		if (!relatedId) {
			return;
		}
		this.navTo("orderDetail", { id: relatedId });
	}

	/**
	 * Link w wierszu linii zamówienia → Entitlement provisionowany z tej linii.
	 * Source: <c>event.getSource()</c> to Link; jego binding context wskazuje na pojedynczą linię
	 * w <c>odata>Lines</c>, czytamy <c>EntitlementId</c>.
	 */
	public onOpenLineEntitlement(event: { getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } }): void {
		const ctx = event.getSource().getBindingContext("odata");
		const id = ctx?.getProperty("EntitlementId") as string | undefined;
		if (!id) {
			return;
		}
		this.navTo("entitlementDetail", { id });
	}

	public onRefresh(): void {
		this.getView().getModel("odata")?.refresh();
		const orderId = (this.getView().getModel("view") as JSONModel).getProperty("/orderId") as string;
		if (orderId) {
			void this.loadHeaderSideData(orderId);
		}
	}

	public override onNavBack(): void {
		this.navTo("orders");
	}

	// === Pay flow ===

	public async onPayPress(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const orderId = view.getProperty("/orderId") as string;
		const orderNumber = view.getProperty("/orderNumber") as string;
		const orderType = view.getProperty("/orderType") as string;
		if (!orderId) {
			return;
		}

		if (!this.payDialog) {
			this.payDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.PayOrderDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.payDialog);
		}

		const bundle = await this.getResourceBundle();
		const info = bundle.getText("orders.pay.info", [orderNumber, orderType]) ?? `Pay ${orderNumber}`;
		(this.byId("payOrderInfo") as Text).setText(info);
		const invoiceInput = this.byId("inpInvoiceNumber") as Input;
		invoiceInput.setValue("");
		invoiceInput.setValueState("None");
		invoiceInput.setValueStateText("");
		this.payDialog.open();
	}

	public onInvoiceNumberChange(): void {
		const invoiceInput = this.byId("inpInvoiceNumber") as Input;
		if (invoiceInput.getValue().trim().length > 0) {
			invoiceInput.setValueState("None");
			invoiceInput.setValueStateText("");
		}
	}

	public onCancelPay(): void {
		this.payDialog?.close();
	}

	public async onConfirmPay(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const orderId = view.getProperty("/orderId") as string;
		if (!this.payDialog || !orderId) {
			return;
		}
		const invoiceInput = this.byId("inpInvoiceNumber") as Input;
		const invoiceNumber = invoiceInput.getValue().trim();
		const bundle = await this.getResourceBundle();

		if (invoiceNumber.length === 0) {
			invoiceInput.setValueState("Error");
			invoiceInput.setValueStateText(bundle.getText("orders.pay.invoiceNumberRequired") ?? "Invoice number is required");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json<void>(`/api/v1/orders/${orderId}/payments`, {
				method: "POST",
				body: JSON.stringify({ invoiceNumber })
			});
			MessageToast.show(bundle.getText("orders.pay.success") ?? "Payment registered");
			this.payDialog.close();
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("orders.pay.failure") ?? "Failed to register payment"));
		}
	}

	// === MarkOverdue flow ===

	public async onMarkOverduePress(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const orderId = view.getProperty("/orderId") as string;
		const orderNumber = view.getProperty("/orderNumber") as string;
		if (!orderId) {
			return;
		}

		if (!this.markOverdueDialog) {
			this.markOverdueDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.MarkOrderOverdueDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.markOverdueDialog);
		}

		const bundle = await this.getResourceBundle();
		const info = bundle.getText("orders.markOverdue.info", [orderNumber])
			?? `Mark order ${orderNumber} as overdue`;
		(this.byId("markOverdueInfo") as Text).setText(info);
		const reasonArea = this.byId("taOverdueReason") as TextArea;
		reasonArea.setValue("");
		reasonArea.setValueState("None");
		reasonArea.setValueStateText("");
		this.markOverdueDialog.open();
	}

	public onOverdueReasonChange(): void {
		const reasonArea = this.byId("taOverdueReason") as TextArea;
		if (reasonArea.getValue().trim().length > 0) {
			reasonArea.setValueState("None");
			reasonArea.setValueStateText("");
		}
	}

	public onCancelMarkOverdue(): void {
		this.markOverdueDialog?.close();
	}

	public async onConfirmMarkOverdue(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const orderId = view.getProperty("/orderId") as string;
		if (!this.markOverdueDialog || !orderId) {
			return;
		}
		const reasonArea = this.byId("taOverdueReason") as TextArea;
		const reason = reasonArea.getValue().trim();
		const bundle = await this.getResourceBundle();

		if (reason.length === 0) {
			reasonArea.setValueState("Error");
			reasonArea.setValueStateText(bundle.getText("orders.markOverdue.reasonRequired") ?? "Reason is required");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json<void>(`/api/v1/orders/${orderId}/mark-overdue`, {
				method: "POST",
				body: JSON.stringify({ reason })
			});
			MessageToast.show(bundle.getText("orders.markOverdue.success") ?? "Order marked as overdue");
			this.markOverdueDialog.close();
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("orders.markOverdue.failure") ?? "Failed to mark as overdue"));
		}
	}

}
